import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { when, pretty } from "./claimsShared";

const CLAIM_TYPES = ["damage_in_transit", "leakage_at_receipt", "short_stock", "expired_received", "near_expiry", "other"];
const PRIORITIES = ["urgent", "normal", "low"];

export default function ClaimsSettingsPage() {
  const [s, setS] = useState(null);
  const [audit, setAudit] = useState([]);
  const [users, setUsers] = useState([]);
  const [channels, setChannels] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/claims/settings").then((r) => {
      const { audit_history, ...rest } = r.data;
      setS(rest); setAudit(audit_history || []);
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Failed to load settings"));
    api.get("/users").then((r) => setUsers(r.data?.users || r.data || [])).catch(() => {});
    api.get("/channels").then((r) => setChannels(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/claims/settings", {
        approval_limit: Number(s.approval_limit),
        claim_window_hours: s.claim_window_hours,
        sla_hours: s.sla_hours,
        evidence_deadline_hours: Number(s.evidence_deadline_hours),
        video_call_deadline_working_days: Number(s.video_call_deadline_working_days),
        watchers: s.watchers || {},
        whatsapp_enabled: !!s.whatsapp_enabled,
      });
      toast.success("Claims settings saved");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally { setBusy(false); }
  };

  if (!s) return <div className="text-sm text-zinc-500" data-testid="claims-settings-loading">Loading settings…</div>;

  const setWindow = (k, v) => setS((x) => ({ ...x, claim_window_hours: { ...x.claim_window_hours, [k]: v === "" ? null : Number(v) } }));
  const setSla = (k, v) => setS((x) => ({ ...x, sla_hours: { ...x.sla_hours, [k]: Number(v) } }));

  return (
    <div className="space-y-5 max-w-5xl" data-testid="claims-settings-page">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-zinc-900">Claim Rules</h1>
        <Button size="sm" onClick={save} disabled={busy} data-testid="save-claims-settings-btn">Save settings</Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-4 grid md:grid-cols-3 gap-4">
        <div>
          <Label className="text-xs">Approval limit (₹)</Label>
          <Input type="number" className="h-9 text-xs" value={s.approval_limit ?? ""}
            onChange={(e) => setS((x) => ({ ...x, approval_limit: e.target.value }))} data-testid="approval-limit-input" />
          <p className="text-[11px] text-zinc-500 mt-1">A reviewer's decision is final up to this claimed value. Above it, Admin decides.</p>
        </div>
        <div>
          <Label className="text-xs">Evidence deadline (hours)</Label>
          <Input type="number" className="h-9 text-xs" value={s.evidence_deadline_hours ?? ""}
            onChange={(e) => setS((x) => ({ ...x, evidence_deadline_hours: e.target.value }))} data-testid="evidence-deadline-input" />
        </div>
        <div>
          <Label className="text-xs">Video-call deadline (working days, Mon–Sat)</Label>
          <Input type="number" className="h-9 text-xs" value={s.video_call_deadline_working_days ?? ""}
            onChange={(e) => setS((x) => ({ ...x, video_call_deadline_working_days: e.target.value }))} data-testid="video-call-deadline-input" />
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="claim-windows-box">
        <h2 className="text-base font-semibold text-zinc-900 mb-2">Claim window by type (hours from goods received)</h2>
        <div className="grid md:grid-cols-3 gap-3">
          {CLAIM_TYPES.map((t) => (
            <div key={t}>
              <Label className="text-xs">{pretty(t)}</Label>
              <Input type="number" className="h-8 text-xs" placeholder="no window"
                value={s.claim_window_hours?.[t] ?? ""} onChange={(e) => setWindow(t, e.target.value)}
                data-testid={`window-${t}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="sla-hours-box">
        <h2 className="text-base font-semibold text-zinc-900 mb-2">SLA hours per priority</h2>
        <div className="grid md:grid-cols-3 gap-3">
          {PRIORITIES.map((p) => (
            <div key={p}>
              <Label className="text-xs">{pretty(p)}</Label>
              <Input type="number" className="h-8 text-xs" value={s.sla_hours?.[p] ?? ""}
                onChange={(e) => setSla(p, e.target.value)} data-testid={`sla-${p}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="watchers-box">
        <h2 className="text-base font-semibold text-zinc-900 mb-2">Watchers per channel (in-app notification only)</h2>
        <div className="space-y-2">
          {channels.map((c) => {
            const key = c.name || c.channel_name || "";
            const chosen = (s.watchers || {})[key] || [];
            return (
              <div key={key} className="flex items-start gap-3 text-xs">
                <span className="w-40 font-medium">{key}</span>
                <select multiple className="flex-1 border border-zinc-200 rounded px-1 py-1 text-xs h-20"
                  value={chosen}
                  onChange={(e) => {
                    const sel = Array.from(e.target.selectedOptions).map((o) => o.value);
                    setS((x) => ({ ...x, watchers: { ...x.watchers, [key]: sel } }));
                  }}
                  data-testid={`watchers-${key}`}>
                  {users.map((u) => (<option key={u._id} value={u._id}>{u.name}</option>))}
                </select>
              </div>
            );
          })}
          {channels.length === 0 && <p className="text-xs text-zinc-400">No channels configured.</p>}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-4 flex items-center justify-between" data-testid="whatsapp-box">
        <div>
          <h2 className="text-base font-semibold text-zinc-900">WhatsApp notifications</h2>
          <p className="text-xs text-zinc-500">No provider is connected yet, so nothing is sent — each attempt is just recorded on the claim.</p>
        </div>
        <Switch checked={!!s.whatsapp_enabled}
          onCheckedChange={(v) => setS((x) => ({ ...x, whatsapp_enabled: v }))} data-testid="whatsapp-toggle" />
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="settings-audit-history">
        <h2 className="text-base font-semibold text-zinc-900 mb-2">Audit history</h2>
        {audit.length === 0 && <p className="text-xs text-zinc-400">No changes recorded yet.</p>}
        <ol className="space-y-1">
          {audit.map((a) => (
            <li key={a._id} className="text-xs text-zinc-600" data-testid="settings-audit-row">
              <strong>{a.field_name}</strong>: {a.old_value} → {a.new_value} · {a.user_name} · {when(a.timestamp)}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
